import { collectMacroTokensFromSchedule as collectMacroTokensFromScheduleSet } from "authoring/routes/macro/lib/macro-token-utils"

export function collectMacroTokensFromSchedule(schedule) {
    return [...collectMacroTokensFromScheduleSet(schedule)]
}

export function collectLinkedMacroIds(ids = [], allMacrosById = {}) {
    const linkedIds = new Set()
    if (!Array.isArray(ids) || ids.length === 0) return linkedIds
    ids.forEach((id) => {
        if (!id) return
        const macro = allMacrosById?.[id]
        const linked = Array.isArray(macro?.linked) ? macro.linked : []
        linked.forEach((linkedId) => {
            if (!linkedId || linkedId === id) return
            linkedIds.add(linkedId)
        })
    })
    return linkedIds
}

export function sortMacrosForSuggestions(macros = [], usageById = {}, linkedMacroIds = []) {
    if (!Array.isArray(macros) || macros.length === 0) return []
    const linkedSet = linkedMacroIds instanceof Set ? linkedMacroIds : new Set(linkedMacroIds)
    return [...macros].sort((left, right) => {
        const leftLinked = linkedSet.has(left?.id)
        const rightLinked = linkedSet.has(right?.id)
        if (leftLinked !== rightLinked) return leftLinked ? -1 : 1
        const leftCount = usageById?.[left?.id] ?? 0
        const rightCount = usageById?.[right?.id] ?? 0
        if (leftCount !== rightCount) return rightCount - leftCount
        return compareMacroLabel(left, right)
    })
}

export function filterMacrosForSuggestions(macros = [], usageById = {}, linkedMacroIds = []) {
    if (!Array.isArray(macros) || macros.length === 0) return []
    const linkedSet = linkedMacroIds instanceof Set ? linkedMacroIds : new Set(linkedMacroIds)
    return macros.filter((macro) => {
        if (!macro?.id) return false
        if (linkedSet.has(macro.id)) return true
        return Object.prototype.hasOwnProperty.call(usageById, macro.id)
    })
}

export function resolveMacrosByIds(ids = [], allMacrosById = {}, macrosByName = {}) {
    const results = []
    const seen = new Set()
    for (const token of ids) {
        const macro = resolveMacroByToken(token, allMacrosById, macrosByName)
        if (!macro) continue
        if (seen.has(macro.id)) continue
        seen.add(macro.id)
        results.push(macro)
    }
    return results
}

export function resolveSelectedMacro(selectedId, allMacrosById = {}) {
    if (!selectedId) return null
    return allMacrosById?.[selectedId] ?? null
}

export function resolveLinkedMacros(linked = [], allMacrosById = {}) {
    if (!Array.isArray(linked) || linked.length === 0) return []
    const results = []
    linked.forEach((id) => {
        const macro = allMacrosById?.[id]
        if (macro) results.push(macro)
    })
    return results
}

export function normalizeSearch(value = "") {
    return value.trim().toLowerCase()
}

export function filterMacros(macros = [], searchValue = "") {
    if (!searchValue) return macros
    return macros.filter((macro) => {
        const name = macro?.name?.toLowerCase() ?? ""
        const id = macro?.id?.toLowerCase() ?? ""
        return name.includes(searchValue) || id.includes(searchValue)
    })
}

export function getAllMacrosEmptyMessage(macros = [], searchValue = "") {
    if (!macros.length) return "No macros found."
    if (!searchValue) return "No macros available."
    return "No macros match your search."
}

export function sortMacrosByUsage(macros = [], usageById = {}) {
    if (!Array.isArray(macros) || macros.length === 0) return []
    return [...macros].sort((left, right) => {
        const leftCount = usageById?.[left?.id] ?? 0
        const rightCount = usageById?.[right?.id] ?? 0
        if (leftCount !== rightCount) return rightCount - leftCount
        return compareMacroLabel(left, right)
    })
}

export function appendTrail(trail = [], selectedId) {
    if (!selectedId) return trail
    if (trail[trail.length - 1] === selectedId) return trail
    return [...trail, selectedId]
}

export function trimTrailTo(trail = [], targetId) {
    const index = trail.findIndex((id) => id === targetId)
    if (index === -1) return trail
    return trail.slice(0, index)
}

export function buildBreadcrumbItems(trail = [], selectedMacro, allMacrosById = {}) {
    if (!selectedMacro?.id) return []
    const crumbs = [...trail, selectedMacro.id]
    return crumbs.map((id, index) => ({
        id,
        label: resolveMacroLabel(id, allMacrosById),
        isLast: index === crumbs.length - 1,
    }))
}

function resolveMacroByToken(token, allMacrosById = {}, macrosByName = {}) {
    if (!token) return null
    const directMatch = allMacrosById?.[token]
    if (directMatch) return directMatch
    const macroId = macrosByName?.[token]
    if (!macroId) return null
    return allMacrosById?.[macroId] ?? null
}

function resolveMacroLabel(id, allMacrosById = {}) {
    if (!id) return "Macro"
    const macro = allMacrosById?.[id]
    return macro?.name ?? id
}

function compareMacroLabel(left, right) {
    const leftLabel = left?.name ?? left?.id ?? ""
    const rightLabel = right?.name ?? right?.id ?? ""
    return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: "base" })
}
